import { Link } from "@tanstack/react-router";
import { MessageSquare, Moon, PanelLeftClose, PanelLeftOpen, Sparkles, Sun } from "lucide-react";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { Tooltip, TooltipProvider } from "../../components/ui/tooltip";
import { useT } from "../../hooks/useT";
import { useUI } from "../../stores/ui";
import { NotificationsPopover } from "./NotificationsPopover";
import { ScopeStrip } from "./ScopeStrip";
import { TopSearchInput } from "./TopSearchInput";

interface TopbarProps {
  onOpenCommandPalette: () => void;
}

export function Topbar({ onOpenCommandPalette }: TopbarProps) {
  const theme = useUI((s) => s.theme);
  const cycleTheme = useUI((s) => s.cycleTheme);
  const toggleSidebar = useUI((s) => s.toggleSidebar);
  const sidebarCollapsed = useUI((s) => s.sidebarCollapsed);
  const aiPanel = useUI((s) => s.aiPanel);
  const toggleAi = useUI((s) => s.toggleAiPanel);
  const chatPanel = useUI((s) => s.chatPanel);
  const toggleChat = useUI((s) => s.toggleChatPanel);
  const t = useT();

  const ThemeIcon = theme === "dark" ? Moon : Sun;

  return (
    <TooltipProvider>
      <header
        data-testid="topbar"
        className="flex h-14 items-center gap-2 overflow-hidden border-b border-(--border-subtle) bg-(--bg-elevated) px-2 sm:gap-3 sm:px-3"
      >
        <Tooltip label={t("shell.toggle_sidebar")} side="bottom">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            aria-label={t("shell.toggle_sidebar")}
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </Button>
        </Tooltip>

        <Link
          to="/"
          className="flex items-center gap-2 font-semibold no-underline"
          aria-label="DocuStudio home"
        >
          <span className="grid h-7 w-7 place-items-center rounded-md bg-(--accent-500) text-white font-bold">
            D
          </span>
          <span className="hidden sm:inline">DocuStudio</span>
        </Link>

        <TopSearchInput
          onOpenCommandPalette={onOpenCommandPalette}
          placeholder={t("common.search_placeholder")}
        />

        <div className="hidden md:block">
          <ScopeStrip />
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <Tooltip
            label={theme === "dark" ? t("shell.theme.light") : t("shell.theme.dark")}
            side="bottom"
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              aria-label="Theme"
              data-testid="theme-toggle"
              data-theme={theme}
            >
              <ThemeIcon size={16} />
            </Button>
          </Tooltip>

          <Tooltip label={t("shell.toggle_ai")} side="bottom">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleAi}
              data-testid="ai-toggle"
              data-state={aiPanel}
              style={{ color: aiPanel === "open" ? "var(--text-primary)" : "var(--text-tertiary)" }}
              aria-label={t("shell.toggle_ai")}
            >
              <Sparkles size={16} />
            </Button>
          </Tooltip>

          <Tooltip label="Chat" side="bottom">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleChat}
              data-testid="chat-toggle"
              data-state={chatPanel}
              style={{
                color: chatPanel === "open" ? "var(--text-primary)" : "var(--text-tertiary)",
              }}
              aria-label="Toggle chat"
            >
              <MessageSquare size={16} />
            </Button>
          </Tooltip>

          <div className="hidden sm:inline-flex">
            <NotificationsPopover />
          </div>

          <Avatar className="hidden sm:flex">
            <AvatarFallback>MO</AvatarFallback>
          </Avatar>
        </div>
      </header>
    </TooltipProvider>
  );
}
